import { useState } from 'react';
import { Divider, Grid, IconButton, MenuItem, Paper, Stack, TextField, Tooltip, Typography } from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MasterDrawer from '../Master/create/MasterDrawer';
import { BillEmi } from '../../types/bill';
import { MasterRow } from '../../types/master';
import { digitsOnly } from '../../utils/clean';

const ID_PROOFS = ['Aadhaar', 'PAN', 'Driving Licence', 'Voter ID', 'Passport'];

interface PropsData {
  emi: BillEmi;
  total: number;
  banks: MasterRow[];
  onChange: (patch: Partial<BillEmi>) => void;
  onBanksChanged: () => void;
}

/**
 * Shown only when the payment type is EMI: which finance company, how much the
 * customer pays at the counter, and the details the company needs on file.
 *
 * <p>Laid out on a twelve-column grid so every box is the same height and sits on
 * the same line. The financed amount is a read-only field rather than loose text,
 * which keeps it in the row instead of floating between the inputs.
 */
const EmiSection = ({ emi, total, banks, onChange, onBanksChanged }: PropsData) => {
  const [addingBank, setAddingBank] = useState(false);
  const downPayment = Number(emi.downPayment) || 0;
  const financed = Math.max(total - downPayment, 0);

  return (
    <Paper sx={{ p: 2, mt: 2, borderLeft: '3px solid', borderColor: 'primary.main' }}>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.5 }}>
        EMI Details
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
        The down payment is what the customer hands over at the counter; the rest is what the bank owes the shop.
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={4}>
          <Stack direction="row" gap={0.5} alignItems="center">
            <TextField
              select
              label="EMI Bank *"
              size="small"
              fullWidth
              value={emi.emiBankId ?? ''}
              onChange={(event) => onChange({ emiBankId: Number(event.target.value) })}
            >
              {banks.map((bank: any) => (
                <MenuItem key={bank.id} value={bank.id}>
                  {bank.bankName}
                </MenuItem>
              ))}
            </TextField>
            <Tooltip title="New finance company">
              <IconButton
                color="primary"
                tabIndex={-1}
                onClick={() => setAddingBank(true)}
                aria-label="New bank"
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, width: 40, height: 40 }}
              >
                <AddCircleOutlineIcon />
              </IconButton>
            </Tooltip>
          </Stack>
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="Down Payment"
            size="small"
            type="number"
            fullWidth
            value={emi.downPayment ?? ''}
            onChange={(event) => onChange({ downPayment: event.target.value === '' ? null : Number(event.target.value) })}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          {/* Read-only, but a field rather than loose text so the row stays level. */}
          <TextField
            label="Financed by bank"
            size="small"
            fullWidth
            value={financed.toFixed(2)}
            InputProps={{ readOnly: true, sx: { fontWeight: 700, color: 'primary.main' } }}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="Tenure (months)"
            size="small"
            type="number"
            fullWidth
            value={emi.tenureMonths ?? ''}
            onChange={(event) => onChange({ tenureMonths: event.target.value === '' ? null : Number(event.target.value) })}
          />
        </Grid>

        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="EMI Amount"
            size="small"
            type="number"
            fullWidth
            value={emi.emiAmount ?? ''}
            onChange={(event) => onChange({ emiAmount: event.target.value === '' ? null : Number(event.target.value) })}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <TextField
            label="Loan / Approval No"
            size="small"
            fullWidth
            value={emi.loanNumber ?? ''}
            onChange={(event) => onChange({ loanNumber: event.target.value })}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
        CUSTOMER DETAILS FOR THE FINANCE COMPANY &mdash; left empty, the bill's customer is used
      </Typography>

      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={emi.customerName ?? ''}
            onChange={(event) => onChange({ customerName: event.target.value })}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={2}>
          <TextField
            label="Mobile"
            size="small"
            fullWidth
            inputProps={{ inputMode: 'numeric', maxLength: 15 }}
            value={emi.mobileNumber ?? ''}
            onChange={(event) => onChange({ mobileNumber: digitsOnly(event.target.value) })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <TextField
            label="Address"
            size="small"
            fullWidth
            value={emi.address ?? ''}
            onChange={(event) => onChange({ address: event.target.value })}
          />
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            select
            label="ID Proof"
            size="small"
            fullWidth
            value={emi.idProofType ?? ''}
            onChange={(event) => onChange({ idProofType: event.target.value })}
          >
            {ID_PROOFS.map((proof) => (
              <MenuItem key={proof} value={proof}>
                {proof}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} sm={3} md={2}>
          <TextField
            label="ID Number"
            size="small"
            fullWidth
            value={emi.idProofNumber ?? ''}
            onChange={(event) => onChange({ idProofNumber: event.target.value })}
          />
        </Grid>
      </Grid>

      {addingBank && (
        <MasterDrawer
          tab="EMI_BANK"
          editData={null}
          closeDrawer={() => {
            setAddingBank(false);
            onBanksChanged();
          }}
        />
      )}
    </Paper>
  );
};

export default EmiSection;
